import React, { useEffect, useState } from "react";
import { Menu, X, CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { authFetch } from "@/lib/authFetch";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import { AffiliateNav } from "@/components/AffiliateNav";
import { HistoryTimeline, KIND_LABEL } from "@/components/HistoryTimeline";
import { loadCandidates } from "@/components/LinkedClientsDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const PersonFilter = ({ value, onChange }) => {
  const [people, setPeople] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { loadCandidates("assigned_user").then(setPeople); }, []);
  if (value) {
    return (
      <div className="flex items-center gap-2 h-10 px-3 rounded-md border text-sm" data-testid="history-person-selected">
        {value.name}<button onClick={() => onChange(null)} aria-label="Clear person" data-testid="history-person-clear"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }
  const hits = q.length > 1 ? people.filter((p) => `${p.name} ${p.email}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8) : [];
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Any person" data-testid="history-person-input" />
      {hits.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-md border bg-white shadow-lg">
          {hits.map((p) => (
            <button key={p.user_id} className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-100" onClick={() => { onChange(p); setQ(""); }} data-testid={`history-person-${p.user_id}`}>
              {p.name} <span className="text-slate-500">{p.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const DateField = ({ value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const date = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <div className="flex gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start font-normal" data-testid={testId}>
            <CalendarDays className="h-4 w-4 mr-2" />{date ? date.toLocaleDateString() : <span className="text-slate-500">Any date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={date} onSelect={(d) => { onChange(d ? toIso(d) : ""); setOpen(false); }} initialFocus />
        </PopoverContent>
      </Popover>
      {value && <Button variant="ghost" size="icon" onClick={() => onChange("")} aria-label="Clear date" data-testid={`${testId}-clear`}><X className="h-4 w-4" /></Button>}
    </div>
  );
};

export default function AffiliateHistory() {
  const { isDarkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [person, setPerson] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [f, setF] = useState({ admin_id: "", kind: "", date_from: "", date_to: "" });
  useEffect(() => {
    authFetch(`${API}/history?limit=1`, { credentials: "include" }).then((r) => r.json()).then((d) => setAdmins(d.admins || [])).catch(() => {});
  }, []);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v === "any" ? "" : v }));

  return (
    <div className={`flex min-h-screen ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={true} />
      <main className="flex-1 min-w-0 md:ml-64 overflow-x-hidden">
        <header className={`sticky top-0 z-30 px-4 md:px-6 py-4 border-b ${isDarkMode ? "bg-black border-[#1a3d18]" : "header-glass"}`}>
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden p-2 rounded-lg hover:bg-slate-100"><Menu className="h-5 w-5" /></button>
            <div>
              <h1 className={`text-xl md:text-2xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`} data-testid="history-page-title">Change History</h1>
              <p className="text-sm text-slate-500">Every affiliate edit and client link change, newest first. History can't be edited or deleted.</p>
            </div>
          </div>
          <AffiliateNav />
        </header>
        <div className="p-4 md:p-6 space-y-4">
          <div className="grid gap-3 md:grid-cols-5 items-end" data-testid="history-filters">
            <div><Label>Person</Label><PersonFilter value={person} onChange={setPerson} /></div>
            <div>
              <Label>Admin</Label>
              <Select value={f.admin_id || "any"} onValueChange={set("admin_id")}>
                <SelectTrigger data-testid="history-admin-filter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any admin</SelectItem>{admins.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type of change</Label>
              <Select value={f.kind || "any"} onValueChange={set("kind")}>
                <SelectTrigger data-testid="history-kind-filter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any type</SelectItem>{Object.entries(KIND_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>From</Label><DateField value={f.date_from} onChange={set("date_from")} testId="history-date-from" /></div>
            <div><Label>To</Label><DateField value={f.date_to} onChange={set("date_to")} testId="history-date-to" /></div>
          </div>
          <div className={`rounded-xl border p-4 md:p-6 ${isDarkMode ? "bg-[#0d1f0c] border-[#1a3d18] text-white" : "bg-white"}`}>
            <HistoryTimeline params={{ ...f, person_id: person?.user_id }} />
          </div>
        </div>
      </main>
    </div>
  );
}
